// src/pages/Resources/SiteSavedDataModal.tsx
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, ListGroup, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaQuestionCircle } from "react-icons/fa";
import { searchUsers, getCostCenterSuggestions, confirmMetadataReview } from "@/services/resourcesApi";

interface SiteSavedData {
  primaryOwnerNtid?: string;
  secondaryOwnerNtid?: string;
  primaryAdminNtid?: string;
  secondaryAdminNtid?: string;
  businessUnit?: string;
  costCenter?: string;
  codeOfConductSensitive?: boolean;
  lastUpdated?: string;
}

interface SiteSavedDataModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (data: SiteSavedData) => Promise<void>;
  initialData?: SiteSavedData | null;
  groupId: string | null; // Reserved for future use
}

interface UserSearchResult {
  id?: string;
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
  email?: string;
  upn?: string;
  employeeDeptId?: string;
}

const getUserEmail = (user: UserSearchResult) =>
  (user.mail || user.userPrincipalName || user.email || user.upn || "").trim();

interface UserLookupInputProps {
  label: string;
  required?: boolean;
  placeholder?: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  onUserSelected?: (user: UserSearchResult) => void;
  onEmailBlur?: (email: string) => void;
}

const UserLookupInput: React.FC<UserLookupInputProps> = ({
  label,
  required = false,
  placeholder = "Type a name or email",
  value,
  error,
  onChange,
  onUserSelected,
  onEmailBlur,
}) => {
  const [query, setQuery] = useState(value || "");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const term = query.trim();
    if (!showDropdown || term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setSearching(true);
        const found = await searchUsers(term);
        if (!cancelled) {
          setResults((found as UserSearchResult[]).slice(0, 8));
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [query, showDropdown]);

  const handleSelect = (user: UserSearchResult) => {
    const email = getUserEmail(user);
    if (!email) return;
    setQuery(email);
    onChange(email);
    onUserSelected?.(user);
    setShowDropdown(false);
    setResults([]);
  };

  return (
    <Form.Group className="user-lookup-field">
      <Form.Label>
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <Form.Control
        type="text"
        value={query}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          onChange(next);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => {
          onEmailBlur?.(query);
          window.setTimeout(() => setShowDropdown(false), 150);
        }}
        isInvalid={!!error}
        placeholder={placeholder}
        autoComplete="off"
      />
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>

      {showDropdown && (
        <div className="user-lookup-dropdown">
          {searching && (
            <div className="user-lookup-status text-muted small">
              <Spinner animation="border" size="sm" className="me-2" />
              Searching...
            </div>
          )}

          {!searching && query.trim().length >= 2 && results.length > 0 && (
            <ListGroup>
              {results.map((user, idx) => {
                const email = getUserEmail(user);
                const displayName = (user.displayName || email || "Unknown User").trim();
                return (
                  <ListGroup.Item
                    key={user.id || `${email}-${idx}`}
                    action
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(user);
                    }}
                    className="user-lookup-item"
                  >
                    <div className="fw-semibold">{displayName}</div>
                    {email && <div className="text-muted small">{email}</div>}
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}

          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <div className="user-lookup-status text-muted small">No users found</div>
          )}
        </div>
      )}
    </Form.Group>
  );
};

const SiteSavedDataModal: React.FC<SiteSavedDataModalProps> = ({
  show,
  onHide,
  onSave,
  initialData,
  groupId,
}) => {
  const [formData, setFormData] = useState<SiteSavedData>({
    primaryOwnerNtid: "",
    secondaryOwnerNtid: "",
    primaryAdminNtid: "",
    secondaryAdminNtid: "",
    businessUnit: "",
    costCenter: "",
    codeOfConductSensitive: false,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [costCenterOptions, setCostCenterOptions] = useState<string[]>([]);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);
  const [showCostCenterSuggestions, setShowCostCenterSuggestions] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (!show) return;

    const loadCostCenters = async () => {
      try {
        setLoadingCostCenters(true);
        setCostCenterOptions(await getCostCenterSuggestions());
      } catch (error) {
        console.error("Cost center lookup failed", error);
        setCostCenterOptions([]);
      } finally {
        setLoadingCostCenters(false);
      }
    };

    void loadCostCenters();
  }, [show]);

  const getEmployeeDeptId = (user: UserSearchResult): string => {
    return (user.employeeDeptId ?? "").toString().trim();
  };

  const handlePrimaryOwnerSelected = (user: UserSearchResult) => {
    const employeeDeptId = getEmployeeDeptId(user);
    if (!employeeDeptId) return;

    setFormData((prev) => ({
      ...prev,
      primaryOwnerNtid: getUserEmail(user) || prev.primaryOwnerNtid || "",
      costCenter: employeeDeptId,
    }));
  };

  const handlePrimaryOwnerBlur = async (email: string) => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail.includes("@")) return;

    try {
      const users = await searchUsers(normalizedEmail) as UserSearchResult[];
      const user = users.find((candidate) =>
        getUserEmail(candidate).toLowerCase() === normalizedEmail.toLowerCase()
      );
      const employeeDeptId = getEmployeeDeptId(user ?? {});

      if (employeeDeptId) {
        setFormData((prev) =>
          prev.primaryOwnerNtid?.toLowerCase() === normalizedEmail.toLowerCase()
            ? { ...prev, costCenter: employeeDeptId }
            : prev
        );
      }
    } catch (error) {
      console.error("Primary owner department lookup failed", error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.primaryOwnerNtid?.trim()) {
      newErrors.primaryOwnerNtid = "Primary Owner is required";
    }
    if (!formData.secondaryOwnerNtid?.trim()) {
      newErrors.secondaryOwnerNtid = "Secondary Owner is required";
    }
    if (!formData.businessUnit?.trim()) {
      newErrors.businessUnit = "Business Unit is required";
    }
    if (!formData.costCenter?.trim()) {
      newErrors.costCenter = "Cost Center is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!groupId) {
      setSaveError("Unable to save metadata because the group ID is missing.");
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await onSave(formData);

      try {
        await confirmMetadataReview(groupId);
      } catch (reviewError) {
        // Metadata is already saved; a failed review stamp must not report the save as failed.
        console.warn("Metadata saved but review confirmation failed:", reviewError);
      }

      onHide();
    } catch (error: any) {
      console.error("Failed to save metadata:", error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save metadata. Please try again.";
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof SiteSavedData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (saveError) setSaveError(null);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleClose = () => {
    setErrors({});
    setSaveError(null);
    onHide();
  };

  const isMissingData = !initialData?.primaryOwnerNtid || !initialData?.secondaryOwnerNtid ||
                        !initialData?.businessUnit || !initialData?.costCenter;
  const filteredCostCenterOptions = costCenterOptions
    .filter((costCenter) => costCenter.toLowerCase().includes((formData.costCenter || "").trim().toLowerCase()))
    .slice(0, 12);

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isMissingData && <i className="fas fa-exclamation-triangle text-danger me-2" />}
          Group Metadata
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {saveError && (
            <div className="alert alert-danger mb-3">
              <strong>Save failed:</strong> {saveError}
            </div>
          )}

          {isMissingData && (
            <div className="alert alert-warning mb-3">
              <strong>Missing Information!</strong> Please complete all required fields.
            </div>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <UserLookupInput
                label="Primary Owner"
                required
                value={formData.primaryOwnerNtid || ""}
                error={errors.primaryOwnerNtid}
                onChange={(val) => handleChange("primaryOwnerNtid", val)}
                onUserSelected={handlePrimaryOwnerSelected}
                onEmailBlur={handlePrimaryOwnerBlur}
              />
            </Col>
            <Col md={6}>
              <UserLookupInput
                label="Secondary Owner"
                required
                value={formData.secondaryOwnerNtid || ""}
                error={errors.secondaryOwnerNtid}
                onChange={(val) => handleChange("secondaryOwnerNtid", val)}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <UserLookupInput
                label="Primary Admin"
                value={formData.primaryAdminNtid || ""}
                onChange={(val) => handleChange("primaryAdminNtid", val)}
              />
            </Col>
            <Col md={6}>
              <UserLookupInput
                label="Secondary Admin"
                value={formData.secondaryAdminNtid || ""}
                onChange={(val) => handleChange("secondaryAdminNtid", val)}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Business Unit <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={formData.businessUnit || ""}
                  onChange={(e) => handleChange("businessUnit", e.target.value)}
                  isInvalid={!!errors.businessUnit}
                >
                  <option value="">Select Business Unit</option>
                  <option value="ENMAX Corporation">ENMAX Corporation</option>
                  <option value="ENMAX Energy">ENMAX Energy</option>
                  <option value="ENMAX Power">ENMAX Power</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.businessUnit}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex align-items-center gap-1">
                  Cost Center / Dept ID <span className="text-danger">*</span>
                  <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={<Tooltip id="metadata-cost-center-help">Cost Center / Dept ID is usually the Cost Center / Dept ID for the Primary Owner. Selecting the Primary Owner will set this value.</Tooltip>}>
                    <button type="button" className="btn btn-link btn-sm p-0 text-muted d-inline-flex align-items-center" aria-label="Cost Center / Dept ID help">
                      <FaQuestionCircle />
                    </button>
                  </OverlayTrigger>
                </Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    value={formData.costCenter || ""}
                    onChange={(e) => {
                      handleChange("costCenter", e.target.value);
                      setShowCostCenterSuggestions(true);
                    }}
                    onFocus={() => setShowCostCenterSuggestions(true)}
                    onBlur={() => window.setTimeout(() => setShowCostCenterSuggestions(false), 150)}
                    isInvalid={!!errors.costCenter}
                    disabled={loadingCostCenters}
                    placeholder={loadingCostCenters ? "Loading Cost Center / Dept IDs..." : "Type or select Cost Center / Dept ID"}
                    autoComplete="off"
                  />
                  {showCostCenterSuggestions && !loadingCostCenters && (
                    <div className="position-absolute bg-white border rounded shadow-sm w-100 mt-1" style={{ zIndex: 1060, maxHeight: 240, overflowY: "auto" }}>
                      {filteredCostCenterOptions.length > 0 ? (
                        filteredCostCenterOptions.map((costCenter) => (
                          <button
                            key={costCenter}
                            type="button"
                            className="dropdown-item py-2"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleChange("costCenter", costCenter);
                              setShowCostCenterSuggestions(false);
                            }}
                          >
                            {costCenter}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 small text-muted">No matching Dept IDs. You can keep the typed value.</div>
                      )}
                    </div>
                  )}
                </div>
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.costCenter}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Code of Conduct Sensitive"
              checked={formData.codeOfConductSensitive || false}
              onChange={(e) => handleChange("codeOfConductSensitive", e.target.checked)}
            />
            <Form.Text className="text-muted">
              Check if this group handles sensitive information
            </Form.Text>
          </Form.Group>

          {formData.lastUpdated && (
            <div className="text-muted small">
              Last updated: {new Date(formData.lastUpdated).toLocaleString()}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save & Close"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SiteSavedDataModal;
